// Team strategy data — author-encoded knowledge that the damage matrix can't
// derive on its own (bring rates, "if you see X bring Y" rules, lead pivots,
// setup scripts, key 1v1 notes). Lives on each saved team as `team.strategy`.
//
// Shape:
//   {
//     bringPriors: { [pokemonName]: number },   // 0..1 bring rate prior
//     triggers: [
//       {
//         id, when: { sees: string[], andAlso?: string[], megaOnly?: bool, archetype?: string },
//         bring: string[],
//         reason: string,
//       }
//     ],
//     leadRules: {
//       defaultLead?: string,
//       avoid: [{ lead: string, into: string[], reason?: string }],
//     },
//     setupScripts: [
//       { mon: string, vs: string[], script: string }
//     ],
//     keyOneVOnes: [
//       { mon: string, vs: string, note: string }
//     ],
//   }

export const ARCHETYPE_OPTIONS = [
  'Stall', 'Rain', 'Sun', 'Sand', 'Snow',
  'Trick Room', 'Tailwind HO', 'Baton Pass', 'Balance',
];

export function emptyStrategy() {
  return {
    bringPriors: {},
    triggers: [],
    leadRules: { defaultLead: '', avoid: [] },
    setupScripts: [],
    keyOneVOnes: [],
  };
}

export function normalizeStrategy(s) {
  const empty = emptyStrategy();
  if (!s || typeof s !== 'object') return empty;
  return {
    bringPriors: s.bringPriors && typeof s.bringPriors === 'object' ? s.bringPriors : {},
    triggers: Array.isArray(s.triggers) ? s.triggers.map(normalizeTrigger).filter(Boolean) : [],
    leadRules: {
      defaultLead: s.leadRules?.defaultLead || '',
      avoid: Array.isArray(s.leadRules?.avoid)
        ? s.leadRules.avoid.filter(a => a && a.lead && Array.isArray(a.into) && a.into.length > 0)
        : [],
    },
    setupScripts: Array.isArray(s.setupScripts)
      ? s.setupScripts.filter(x => x && x.mon && Array.isArray(x.vs) && x.vs.length > 0 && x.script)
      : [],
    keyOneVOnes: Array.isArray(s.keyOneVOnes)
      ? s.keyOneVOnes.filter(x => x && x.mon && x.vs && x.note)
      : [],
  };
}

function normalizeTrigger(t) {
  if (!t || typeof t !== 'object') return null;
  const when = t.when || {};
  const sees = Array.isArray(when.sees) ? when.sees.filter(Boolean) : [];
  const andAlso = Array.isArray(when.andAlso) ? when.andAlso.filter(Boolean) : [];
  const archetype = typeof when.archetype === 'string' ? when.archetype : '';
  if (sees.length === 0 && andAlso.length === 0 && !archetype) return null;
  const bring = Array.isArray(t.bring) ? t.bring.filter(Boolean) : [];
  if (bring.length === 0) return null;
  return {
    id: t.id || String(Math.random()).slice(2),
    when: { sees, andAlso, megaOnly: !!when.megaOnly, archetype },
    bring,
    reason: t.reason || '',
  };
}

// Returns triggers whose conditions match this opponent team / detected
// archetype. Each match includes the original trigger plus the matched-on
// opponent name so the advisor can attribute the rule.
export function evaluateTriggers(strategy, opponentTeam, archetype) {
  const norm = normalizeStrategy(strategy);
  const oppNames = new Set((opponentTeam || []).map(p => p.name));
  const oppByName = new Map((opponentTeam || []).map(p => [p.name, p]));
  const matches = [];

  for (const t of norm.triggers) {
    const w = t.when;
    let matchedOn = null;

    // Archetype match (independent of `sees`).
    if (w.archetype && archetype && archetype.toLowerCase().includes(w.archetype.toLowerCase())) {
      matchedOn = `archetype: ${w.archetype}`;
    }

    // `sees` — at least one named pokemon must be on the opp team.
    if (w.sees.length > 0) {
      const seen = w.sees.find(name => {
        if (!oppNames.has(name)) return false;
        if (w.megaOnly) {
          const opp = oppByName.get(name);
          // Mega only: require holding a mega stone OR the name being a mega form.
          const item = opp?.item || '';
          const isMega = item.toLowerCase().includes('ite') || /^Mega /.test(name);
          if (!isMega) return false;
        }
        return true;
      });
      if (!seen) continue;
      matchedOn = matchedOn || seen;
    }

    // `andAlso` — every named pokemon also required.
    if (w.andAlso.length > 0) {
      const allPresent = w.andAlso.every(n => oppNames.has(n));
      if (!allPresent) continue;
    }

    if (!matchedOn) continue;
    matches.push({ trigger: t, matchedOn });
  }

  return matches;
}

// Returns the prior bring rate (0..1) the user set for this Pokemon.
// Default to 0.5 (neutral) when unset.
export function getBringPrior(strategy, pokemonName) {
  const norm = normalizeStrategy(strategy);
  const v = norm.bringPriors[pokemonName];
  if (typeof v !== 'number' || isNaN(v)) return 0.5;
  return Math.max(0, Math.min(1, v));
}

// Lookup helpers used by the advisor's lead picker.
export function shouldAvoidLead(strategy, leadName, oppLeadNames) {
  const norm = normalizeStrategy(strategy);
  for (const rule of norm.leadRules.avoid) {
    if (rule.lead !== leadName) continue;
    const intoSet = new Set(rule.into);
    const hit = oppLeadNames.find(n => intoSet.has(n));
    if (hit) return { lead: leadName, into: hit, reason: rule.reason || `Don't lead ${leadName} into ${hit}` };
  }
  return null;
}

export function findSetupScripts(strategy, monName, oppNames) {
  const norm = normalizeStrategy(strategy);
  const set = new Set(oppNames || []);
  return norm.setupScripts.filter(s => s.mon === monName && s.vs.some(v => set.has(v)));
}

export function findKeyOneVOnes(strategy, monName, oppNames) {
  const norm = normalizeStrategy(strategy);
  const set = new Set(oppNames || []);
  return norm.keyOneVOnes.filter(s => s.mon === monName && set.has(s.vs));
}
